import {Header} from "@/components/layouts/main/Header";
import {Chat} from "@/pages/common/ChatGame/Chat";
import {Controls} from "@/pages/common/ChatGame/Controls";
import {Timer} from "@/pages/common/ChatGame/Timer";
import {
  clearChatState,
  createCertificateSession,
  createProductSession,
  createScenarioSession,
  getQuestion,
  setServerStatus,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useReducer, useRef} from "react";

import {useAnamAvatar} from "@hooks/AnamAvatar";
import {useChatTime} from "@hooks/ChatTime";
import {LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE} from "@hooks/HeygenAvatar/utils";
import doctorFemaleImage from "@images/general/doctor_female.png";
import doctorMaleImage from "@images/general/doctor_male.png";
import patientImage from "@images/general/patient.png";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {AiFeedbackPopup} from "@popups/simulationPopups/AiFeedbackPopup";
import {LevelPopup} from "@popups/simulationPopups/LevelPopup";
import {MicrophonePermissionPopup} from "@popups/simulationPopups/MicrophonePermissionPopup";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {TrainerBlueChromaVideo} from "@ui/general/TrainerBlueChromaVideo";
import {Spinner} from "@ui/general/Spinner";
import {RootIcon} from "@ui/icons/RootIcon";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate, useParams} from "react-router-dom";

import styles from "../styles.module.scss";

export const ChatAnamGame = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {openPopup, closePopup} = usePopup();
  const sessionType = params?.session;
  const sessionLevel = params?.level;
  const sessionId = params?.id;
  const avatarType = location?.state?.level?.avatar_type ?? "";
  const normalizedAvatarType = String(avatarType).toLowerCase();
  const isFemaleDoctorAvatar = normalizedAvatarType.includes("female");
  const isMaleDoctorAvatar =
    normalizedAvatarType.includes("male") && !isFemaleDoctorAvatar;
  const doctorPlaceholderImage = isMaleDoctorAvatar
    ? doctorMaleImage
    : doctorFemaleImage;
  const doctorPlaceholderAlt = isMaleDoctorAvatar
    ? "doctor-male"
    : "doctor-female";

  const isMedical = window.location.host.includes("ava-medical.vivanti.eu");

  const CHAT_LIFECYCLE = {
    CHOOSING: "choosing",
    STARTING: "starting",
    ACTIVE: "active",
    FEEDBACK: "feedback",
  };

  const [lifecycle, lifecycleDispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case "START":
          return {status: CHAT_LIFECYCLE.STARTING, mode: action.mode};
        case "ACTIVE":
          return {...state, status: CHAT_LIFECYCLE.ACTIVE};
        case "FEEDBACK":
          return {...state, status: CHAT_LIFECYCLE.FEEDBACK};
        case "RESET":
          return {
            status: CHAT_LIFECYCLE.CHOOSING,
            mode: action.mode ?? state.mode ?? null,
          };
        default:
          return state;
      }
    },
    {
      status: CHAT_LIFECYCLE.CHOOSING,
      mode: null,
    },
  );

  const isSessionInitialized = lifecycle.status !== CHAT_LIFECYCLE.CHOOSING;
  const hasAvatar = lifecycle.mode === "avatar" && isSessionInitialized;
  const isFeedbackPopupOpen = lifecycle.status === CHAT_LIFECYCLE.FEEDBACK;
  const isInitialProductQuestionRequestedRef = useRef(false);
  const forceFeedbackOnQuitRef = useRef(false);

  const {
    volume,
    messages,
    product_session_id,
    scenario_session_id,
    certificate_session_id,
    score,
    questions,
    sessionState,
    error,
    serverStatus,
    sessionDurationSec,
  } = useSelector((state) => state.ml.chat);

  const hasSimulationSession = Boolean(
    product_session_id ||
      scenario_session_id ||
      certificate_session_id,
  );

  const sessionDurationMs =
    sessionDurationSec != null && Number.isFinite(sessionDurationSec)
      ? sessionDurationSec * 1000
      : null;

  const [chatTime, , restart] = useChatTime(sessionDurationMs);

  const {isMobile} = useSelector((state) => state.common.settings);

  const {
    avatarSessionState,
    isAvatarTalking,
    mediaStream,
    videoElementId,
    AvatarSessionState,
    ensureMicrophonePermission,
    initAnamWithToken,
    stopAnam,
    stopVoice,
    showAnamError,
    clearAllAnamState,
  } = useAnamAvatar(openPopup, isSessionInitialized, hasAvatar, volume);

  const initSession = useCallback(async () => {
    try {
      isInitialProductQuestionRequestedRef.current = false;

      switch (sessionType) {
        case "scenario":
          await dispatch(
            createScenarioSession({
              level: sessionLevel,
              scenario_id: sessionId,
            }),
          );
          break;

        case "product":
          await dispatch(
            createProductSession({
              level: sessionLevel,
              product_id: sessionId,
            }),
          );
          break;

        case "certification":
          await dispatch(
            createCertificateSession({
              level: "certification",
              scenario_id: sessionId,
              session_format: sessionLevel,
            }),
          );
          break;

        default:
          throw new Error("Invalid session type");
      }
    } catch (error) {
      console.log("Session initialization error: ", error);
      dispatch(setServerStatus("error"));
    } finally {
      dispatch(setServerStatus(""));
    }
  }, [dispatch, sessionId, sessionLevel, sessionType]);

  const isMicrophonePermissionError = (error) =>
    error?.code === LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE ||
    error?.cause?.code === LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE ||
    error?.name === "NotAllowedError" ||
    error?.cause?.name === "NotAllowedError";

  const startSessionFlow = useCallback(
    async (mode) => {
      closePopup();
      lifecycleDispatch({type: "START", mode});

      try {
        if (mode === "avatar") {
          await ensureMicrophonePermission();
        }
        await initSession();
        if (mode === "avatar") {
          await initAnamWithToken();
        }
        lifecycleDispatch({type: "ACTIVE"});
        return {ok: true};
      } catch (error) {
        if (mode === "avatar" && isMicrophonePermissionError(error)) {
          lifecycleDispatch({type: "RESET", mode});
          return {ok: false, reason: "microphone_denied"};
        }
        lifecycleDispatch({type: "RESET", mode});
        return {ok: false, reason: "error", error};
      }
    },
    [closePopup, ensureMicrophonePermission, initAnamWithToken, initSession],
  );

  const requestMicrophonePermissionForPopup = useCallback(async () => {
    try {
      await ensureMicrophonePermission();
      return true;
    } catch (_error) {
      return false;
    }
  }, [ensureMicrophonePermission]);

  const openMicrophonePermissionPopup = useCallback(() => {
    openPopup(
      <MicrophonePermissionPopup
        requestMicrophoneAccess={requestMicrophonePermissionForPopup}
        continueWithAvatar={async () => {
          const result = await startSessionFlow("avatar");
          if (result?.reason === "error") {
            showAnamError(handleTryAgain, handleQuit, result.error);
          }
        }}
        continueWithoutAvatar={() => startSessionFlow("text")}
      />,
      {closeButton: false},
    );
  }, [
    openPopup,
    requestMicrophonePermissionForPopup,
    startSessionFlow,
    showAnamError,
  ]);

  const handleClickQuitBtn = async () => {
    try {
      if (product_session_id) {
        if (messages.length > 2) {
          forceFeedbackOnQuitRef.current = true;
          restart(Date.now(), false);
        } else {
          await dispatch(clearChatState());
          lifecycleDispatch({type: "RESET"});
          navigate(-1);
        }
      } else if (scenario_session_id || certificate_session_id) {
        if (messages.length > 1) {
          restart(Date.now(), false);
        } else {
          await dispatch(clearChatState());
          lifecycleDispatch({type: "RESET"});
          navigate(-1);
        }
      } else {
        await dispatch(clearChatState());
        lifecycleDispatch({type: "RESET"});
        navigate(-1);
      }
    } catch (error) {
      console.error("Error handling quit button:", error);
    }
  };

  const handleQuit = useCallback(async () => {
    try {
      await dispatch(clearChatState());
      lifecycleDispatch({type: "RESET"});
      closePopup();
      navigate(-1);
    } catch (error) {
      console.log("Exit error: ", error);
      dispatch(setServerStatus("error"));
    } finally {
      dispatch(setServerStatus(""));
    }
  }, [dispatch, closePopup, navigate]);

  const handleTryAgain = useCallback(async () => {
    try {
      closePopup();
      await clearAllAnamState();
      lifecycleDispatch({type: "RESET"});
      const mode = lifecycle.mode || "text";
      const result = await startSessionFlow(mode);
      if (result?.reason === "microphone_denied") {
        openMicrophonePermissionPopup();
        return;
      }
      if (result?.reason === "error") {
        showAnamError(handleTryAgain, handleQuit, result.error);
        return;
      }
    } catch (error) {
      console.log("Retry error: ", error);
      dispatch(setServerStatus("error"));
    } finally {
      dispatch(setServerStatus(""));
    }
  }, [
    closePopup,
    clearAllAnamState,
    lifecycle.mode,
    startSessionFlow,
    openMicrophonePermissionPopup,
    showAnamError,
  ]);

  useEffect(() => {
    openPopup(
      <LevelPopup
        close={() => startSessionFlow("text")}
        closeWithAvatar={async () => {
          const result = await startSessionFlow("avatar");
          if (result?.reason === "microphone_denied") {
            openMicrophonePermissionPopup();
            return;
          }
          if (result?.reason === "error") {
            showAnamError(handleTryAgain, handleQuit, result.error);
          }
        }}
        item={location?.state?.level}
      />,
      {
        onClose: () => navigate(-1),
      },
    );

    return () => {
      closePopup();
    };
  }, []);

  useEffect(() => {
    if (lifecycle.status !== CHAT_LIFECYCLE.ACTIVE) {
      return;
    }

    const userMessages = messages.filter((m) => m?.sender === "user").length;
    const botMessages = messages.filter((m) => m?.sender === "bot").length - 1;

    let shouldShow = false;
    const stopReasons = [];

    switch (true) {
      case scenario_session_id && score === 0:
        stopReasons.push("scenario_score_zero");
        shouldShow = true;
        break;
      case (scenario_session_id ||
        certificate_session_id ||
        product_session_id) &&
        sessionState === "ended":
        stopReasons.push("session_state_ended");
        shouldShow = true;
        break;
      case product_session_id && forceFeedbackOnQuitRef.current:
        stopReasons.push("product_manual_quit");
        shouldShow = true;
        break;
      case (scenario_session_id || certificate_session_id) && chatTime === 0:
        stopReasons.push("scenario_or_cert_time_zero");
        shouldShow = true;
        break;
      case product_session_id && chatTime === 0:
        stopReasons.push("product_time_zero");
        shouldShow = true;
        break;
      default:
        shouldShow = false;
    }

    if (shouldShow) {
      const forceFeedback = forceFeedbackOnQuitRef.current;
      if (
        avatarSessionState === AvatarSessionState.CONNECTED &&
        !stopVoice &&
        !forceFeedback
      ) {
        console.warn("[Anam] feedback gate blocked by stopVoice=false", {
          reasons: stopReasons,
          sessionState,
          chatTime,
          questions,
          userMessages,
          botMessages,
          stopVoice,
          avatarSessionState,
        });
        return;
      }

      if (!isFeedbackPopupOpen) {
        if (avatarSessionState === AvatarSessionState.CONNECTED) {
          console.warn("[Anam] stopAnam before feedback popup", {
            reasons: stopReasons,
            sessionState,
            chatTime,
            questions,
            userMessages,
            botMessages,
            stopVoice,
          });
          stopAnam().then(() => {
            forceFeedbackOnQuitRef.current = false;
            openPopup(
              <AiFeedbackPopup
                again={handleTryAgain}
                quit={handleQuit}
                withFeedback={userMessages !== 0}
              />,
              {
                closeButton: false,
              },
            );
            lifecycleDispatch({type: "FEEDBACK"});
          });
        } else {
          console.warn("[Anam] open feedback popup without active avatar", {
            reasons: stopReasons,
            sessionState,
            chatTime,
            questions,
            userMessages,
            botMessages,
            stopVoice,
            avatarSessionState,
          });
          openPopup(
            <AiFeedbackPopup
              again={handleTryAgain}
              quit={handleQuit}
              withFeedback={userMessages !== 0}
            />,
            {
              closeButton: false,
            },
          );
          forceFeedbackOnQuitRef.current = false;
          lifecycleDispatch({type: "FEEDBACK"});
        }
      }
    }
  }, [
    lifecycle.status,
    chatTime,
    questions,
    score,
    sessionState,
    scenario_session_id,
    product_session_id,
    certificate_session_id,
    messages,
    avatarSessionState,
    stopVoice,
    stopAnam,
    openPopup,
    handleTryAgain,
    handleQuit,
    isFeedbackPopupOpen,
    AvatarSessionState,
  ]);

  useEffect(() => {
    if (
      !product_session_id ||
      messages.length !== 1 ||
      isInitialProductQuestionRequestedRef.current
    ) {
      return;
    }

    const requestInitialQuestion = () => {
      if (isInitialProductQuestionRequestedRef.current) return;
      isInitialProductQuestionRequestedRef.current = true;
      dispatch(getQuestion({id: product_session_id}));
    };

    if (hasAvatar) {
      if (avatarSessionState !== AvatarSessionState.CONNECTED || !stopVoice)
        return;
      requestInitialQuestion();
      return;
    }

    requestInitialQuestion();
  }, [
    avatarSessionState,
    hasAvatar,
    product_session_id,
    stopVoice,
    messages.length,
    dispatch,
  ]);

  const anamVideoConnectingStyle =
    avatarSessionState === AvatarSessionState.CONNECTING
      ? {
          position: "absolute",
          inset: 0,
          opacity: 0,
          pointerEvents: "none",
        }
      : undefined;

  return (
    <div className={`${styles.gameWrapper} ${styles.gameWrapperAnam}`}>
      <Header />

      <div className={"container-big flex min-h-0 flex-1 phone:p-0"}>
        <div className={styles.game}>
          <div
            className={`${styles.doctor} ${isMedical ? styles.doctorMedical : styles.doctorTrainer} ${styles.doctorAnam}`}
          >
            {hasAvatar && hasSimulationSession && error === "" ? (
              <>
                {avatarSessionState === AvatarSessionState.CONNECTING && (
                  <>
                    <Spinner
                      width={80}
                      height={80}
                    />

                    <img
                      className={styles.doctorImage}
                      src={isMedical ? patientImage : doctorPlaceholderImage}
                      alt={isMedical ? "patient" : doctorPlaceholderAlt}
                    />
                  </>
                )}

                <div style={anamVideoConnectingStyle}>
                  <TrainerBlueChromaVideo
                    videoRef={mediaStream}
                    videoId={videoElementId}
                    isConnected={
                      avatarSessionState === AvatarSessionState.CONNECTED
                    }
                    videoProps={{
                      autoPlay: true,
                      playsInline: true,
                      muted: false,
                    }}
                    wrapperClassName={`${styles.avatarWrapper} ${isMedical ? styles.avatarWrapperMedical : styles.avatarWrapperTrainer} ${styles.avatarWrapperAnam}`}
                    playerClassName={`${styles.avatarPlayer} ${isMedical ? styles.avatarPlayerMedical : styles.avatarPlayerTrainer} ${styles.avatarPlayerAnam}`}
                  />
                </div>

                {avatarSessionState === AvatarSessionState.CONNECTED && (
                  <>
                    <Controls />

                    <div
                      onClick={handleClickQuitBtn}
                      className={`${styles.quitBtn} ${isMedical ? styles.quitBtnMedical : styles.quitBtnTrainer} ${styles.quitBtnAnam}`}
                      style={
                        serverStatus === "loading" || isAvatarTalking
                          ? {
                              opacity: 0.5,
                              pointerEvents: "none",
                            }
                          : {
                              opacity: 1,
                              pointerEvents: "auto",
                            }
                      }
                    >
                      <i>
                        <RootIcon
                          width={32}
                          height={32}
                          id={"quit"}
                        />
                      </i>

                      {!isMobile && (
                        <RootDesc>
                          <span>{t("button.exit")}</span>
                        </RootDesc>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <img
                  className={styles.doctorImage}
                  src={isMedical ? patientImage : doctorPlaceholderImage}
                  alt={isMedical ? "patient" : doctorPlaceholderAlt}
                />

                <div
                  onClick={() => handleClickQuitBtn(restart)}
                  className={`${styles.quitBtn} ${isMedical ? styles.quitBtnMedical : styles.quitBtnTrainer} ${styles.quitBtnAnam}`}
                  style={
                    serverStatus === "loading" || isAvatarTalking
                      ? {
                          opacity: 0.5,
                          pointerEvents: "none",
                        }
                      : {
                          opacity: 1,
                          pointerEvents: "auto",
                        }
                  }
                >
                  <i>
                    <RootIcon
                      width={32}
                      height={32}
                      id={"quit"}
                    />
                  </i>

                  {!isMobile && (
                    <RootDesc>
                      <span>{t("button.exit")}</span>
                    </RootDesc>
                  )}
                </div>
              </>
            )}
          </div>

          <Timer time={chatTime} />

          <div className={styles.gameChatWrapper}>
            <Chat
              time={chatTime}
              hasAvatar={hasAvatar}
              isAvatarTalking={isAvatarTalking}
              avatarSessionState={avatarSessionState}
              AvatarSessionState={AvatarSessionState}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
